import { ExpandLess } from '@mui/icons-material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Box, Button, Menu, MenuItem } from '@mui/material'
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'

interface ITripTypeSelectorProps {
    isRoundTrip?: boolean
    onTripTypeChange?: (isRoundTrip: boolean) => void
    active: boolean
    onActivate: () => void
    onClose: () => void
}

export const TripTypeSelector: React.FC<ITripTypeSelectorProps> = ({
    isRoundTrip,
    onTripTypeChange,
    active,
    onActivate,
    onClose
}) => {
    const t = useTranslations('common')
    const tripType = isRoundTrip ? 'roundtrip' : 'oneway'
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

    const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (active) {
            // If already active, close it
            setAnchorEl(null)
            onClose()
        } else {
            // If not active, open it
            setAnchorEl(event.currentTarget)
            onActivate()
        }
    }

    const handleMenuClose = () => {
        setAnchorEl(null)
        onClose()
    }

    const handleTripTypeSelect = (isRoundTripValue: boolean) => {
        onTripTypeChange?.(isRoundTripValue)
        setAnchorEl(null)
        onClose()
    }

    // Set anchor element when component becomes active
    useEffect(() => {
        if (active && !anchorEl) {
            // If active but no anchor element, we need to find the button
            // This handles cases where another component triggered this to be active
            const button = document.querySelector('[data-testid="triptype-button"]') as HTMLElement
            if (button) {
                setAnchorEl(button)
            }
        } else if (!active) {
            setAnchorEl(null)
        }
    }, [active, anchorEl])

    return (
        <Box sx={{ pr: 0.5 }}>
            <Button
                data-testid="triptype-button"
                variant="text"
                onClick={handleButtonClick}
                sx={{
                    minWidth: 'max-content',
                    borderRadius: 2,
                    textTransform: 'none',
                    '&:hover': {
                        backgroundColor: 'transparent'
                    }
                }}
                endIcon={active ? <ExpandLess /> : <ExpandMoreIcon />}
            >
                {tripType === 'oneway' ? t('oneWay') : t('roundTrip')}
            </Button>

            <Menu
                anchorEl={anchorEl}
                open={active && Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                sx={{ top: 8 }}
            >
                <MenuItem selected={tripType === 'oneway'} onClick={() => handleTripTypeSelect(false)}>
                    {t('oneWay')}
                </MenuItem>
                <MenuItem selected={tripType === 'roundtrip'} onClick={() => handleTripTypeSelect(true)}>
                    {t('roundTrip')}
                </MenuItem>
            </Menu>
        </Box>
    )
}
